import { gql, useLazyQuery } from '@apollo/client';
import {
	ReferralEmailSearch_GetUserEmails,
	ReferralEmailSearch_GetUserEmailsVariables,
	UserFilterInput,
} from '@app/codegen';
import { AppIcon, AppModal, useAppState, useAppTheme } from '@itrustcapital/ui';
import { Button, Input, Layout, ListItem, Text, TopNavigation } from '@ui-kitten/components';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ScrollView, StatusBar, StyleSheet, TouchableOpacity, View } from 'react-native';

import { ReferralForm } from './Referral';

export const REFERRAL_EMAIL_SEARCH_GET_USER_EMAILS = gql`
	query ReferralEmailSearch_GetUserEmails($where: UserFilterInput!, $first: Int, $after: String) {
		users(where: $where, first: $first, after: $after) {
			nodes {
				id
				email
				referralCode
			}
			pageInfo {
				hasNextPage
				startCursor
				endCursor
			}
			totalCount
		}
	}
`;

export interface AppSelectorProps {
	form: UseFormReturn<ReferralForm>;
	disabled: boolean;
}

export function ReferralEmailSearch(props: AppSelectorProps) {
	const theme = useAppTheme();
	const styles = useCustomStyles(theme);
	const showSelect = useAppState(false);
	const selected = useAppState<any>(null);
	const searchTerm = useAppState('');

	const whereValue = useAppState<UserFilterInput>({});

	const [getUserEmails, { data: userEmailsQuery }] = useLazyQuery<
		ReferralEmailSearch_GetUserEmails,
		ReferralEmailSearch_GetUserEmailsVariables
	>(REFERRAL_EMAIL_SEARCH_GET_USER_EMAILS, {
		variables: {
			where: whereValue.get,
			first: 50,
		},

		fetchPolicy: 'network-only',
	});

	function onSelect(item: any) {
		selected.set(item);
	}

	const handleEmailSelection = (selected: {
		email: string;
		id: number;
		referralCode: string;
	}) => {
		const [email] = selected?.email?.split('-');

		const selectedUser = userEmailsQuery?.users?.nodes?.find((user) => user?.email === email);

		if (selectedUser) {
			props?.form.setValue(
				'referredEmail',
				`${selectedUser.email}-${selectedUser.referralCode?.toUpperCase()}`
			);
			props?.form.setValue('selectedReferralId', selectedUser.id);
		}
	};

	return (
		<>
			<AppModal
				animationType="slide"
				hardwareAccelerated={true}
				presentationStyle="overFullScreen"
				style={styles.modal}
				visible={showSelect.get}
			>
				<StatusBar barStyle="light-content" />
				<Layout style={styles.container}>
					<TopNavigation
						accessoryLeft={() => (
							<Button
								appearance="ghost"
								status="basic"
								onPress={() => onSelect(undefined)}
							>
								Clear
							</Button>
						)}
						accessoryRight={() => (
							<Button
								appearance="ghost"
								onPress={() => {
									showSelect.set(false);
									handleEmailSelection(selected.get);
								}}
							>
								Done
							</Button>
						)}
						alignment="center"
					/>
					<View style={styles.body}>
						<Text appearance="hint" category="p2" style={styles.caption}>
							Search for Email
						</Text>
						<Input
							accessoryLeft={() => <AppIcon lib="ev" name="search" size={24} />}
							accessoryRight={
								searchTerm.get.length > 0
									? () => (
											<Button
												size="tiny"
												status="basic"
												onPress={() => searchTerm.set('')}
											>
												Clear
											</Button>
									  )
									: undefined
							}
							placeholder="Search for an email"
							style={styles.filter}
							value={searchTerm.get}
							onChangeText={searchTerm.set}
							onSubmitEditing={() => {
								whereValue.set({
									...whereValue.get,
									or: [
										{
											email: {
												contains: searchTerm.get,
											},
										},
									],
								});
								getUserEmails();
							}}
						/>
					</View>
					<ScrollView scrollIndicatorInsets={{ right: 0 }}>
						<View style={styles.scrollContainer}>
							{(userEmailsQuery?.users?.nodes || []).map((item) => (
								<ListItem
									accessoryRight={
										selected.get === item
											? () => (
													<AppIcon
														lib="fe"
														name="check"
														style={styles.check}
													/>
											  )
											: undefined
									}
									key={item?.id}
									style={styles.item}
									title={() => (
										<Text
											appearance="hint"
											status={selected.get === item ? 'primary' : undefined}
										>
											{`${item?.email?.toString()}-${item?.referralCode?.toUpperCase()}`}
										</Text>
									)}
									onPress={() => onSelect(item)}
								/>
							))}
						</View>
					</ScrollView>
				</Layout>
			</AppModal>
			<TouchableOpacity disabled={props.disabled} onPress={() => showSelect.set(true)}>
				<AppIcon
					color={props.disabled ? theme['color-info-disabled'] : undefined}
					lib="matc"
					name="selection-search"
					size={30}
				/>
			</TouchableOpacity>
		</>
	);
}

function useCustomStyles(theme: ReturnType<typeof useAppTheme>) {
	return StyleSheet.create({
		modal: {
			justifyContent: 'center',
			alignItems: 'center',
			padding: 0,
			margin: 0,
		},
		container: {
			borderRadius: 30,
			width: '95%',
			height: '100%',
			maxWidth: 400,
			maxHeight: 600,
			overflow: 'hidden',
		},
		body: {
			padding: 25,
			paddingTop: 0,
			paddingBottom: 0,
		},
		scrollContainer: {
			padding: 25,
			paddingTop: 0,
			marginBottom: 150,
		},
		caption: {
			marginBottom: 10,
		},
		filter: {
			marginBottom: 10,
		},
		item: {
			borderBottomWidth: StyleSheet.hairlineWidth,
			borderBottomColor: theme['color-basic-transparent-500'],
			borderRadius: 3,
		},
		right: {
			color: theme['text-hint-color'],
		},
		check: {
			fontSize: 20,
			color: theme['color-primary-500'],
		},
	});
}
